import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UMSSBlueDark } from '../utils/colors';
import Header from "../components/Header";
import { app } from '../firebase';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc } from "firebase/firestore";
import emailjs from 'emailjs-com';

const sendCancellationEmail = (appointmentInfo) => {
  const { patient, dateTime, cancellationReason } = appointmentInfo;
  const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_CANCELLATION_ID;
  const userID = process.env.REACT_APP_EMAILJS_USER_ID;

  console.log("serviceID:", serviceID);
  console.log("templateID:", templateID);
  console.log("userID:", userID);

  if (!serviceID || !templateID || !userID) {
    console.error("Missing EmailJS configuration. Please check your environment variables.");
    return Promise.reject(new Error("Email service not properly configured"));
  }

  // Extract patient name: try patient.patientName, or fallback to appointmentInfo.patientName
  const patientName = (patient && patient.firstName && patient.lastName) ? `${patient.firstName} ${patient.lastName}` : appointmentInfo.patientName || "Patient";

  if (!patient || !patient.email) {
    console.error("Patient email is missing.");
    return Promise.reject(new Error("Patient email is missing"));
  }

  // Convert Firestore timestamp properly
  const dateObj = dateTime && dateTime.toDate ? dateTime.toDate() : new Date(dateTime);

  const templateParams = {
    to_email: patient.email,
    patient_name: patientName,
    appointment_date: dateObj ? dateObj.toLocaleDateString() : "N/A",
    appointment_time: dateObj ? dateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : "N/A",
    location_name: appointmentInfo.location?.name || "N/A",
    location_address: appointmentInfo.location?.address || "N/A",
    reschedule_url: appointmentInfo.rescheduleURL || "",
    cancellation_reason: cancellationReason || "No reason provided"
  };

  return emailjs.send(serviceID, templateID, templateParams, userID)
    .then(response => {
      console.log("Cancellation email sent successfully:", response.status, response.text);
      return response;
    })
    .catch(error => {
      console.error("Failed to send cancellation email. Details:", error);
      throw error;
    });
};


const CancelPage = () => {
  const [loading, setLoading] = useState(true);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState(false);
  const [officeID, setOfficeID] = useState(""); // Define officeID as state variable
  
  const location = useLocation();
  
  useEffect(() => {
    // Get appointment ID from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const appointmentId = queryParams.get('appointmentId');

    const db = getFirestore(app);

    if (appointmentId) {
      const fetchAppointmentDetails = async (appointmentId) => {
      setLoading(true);
      try {
        // Get all offices
        const officesRef = collection(db, 'offices');
        const officesSnapshot = await getDocs(officesRef);
        
        for (const officeDoc of officesSnapshot.docs) {
        const officeId = officeDoc.id;
        const officeData = officeDoc.data();

        // Construct the path to the appointments subcollection for the current office
        const appointmentDocRef = doc(db, `offices/${officeId}/appointments`, appointmentId);
        
        // Get the appointment document
        const appointmentSnapshot = await getDoc(appointmentDocRef);

        if (appointmentSnapshot.exists()) {
          // Appointment found, extract the data
          const appointmentData = appointmentSnapshot.data();

          setOfficeID(officeId); // Update using state setter

          // Check if the appointment is booked
          if (!appointmentData.booked) {
          console.log("Appointment is not booked, cancellation not allowed.");
          setLoading(false);
          return; // Exit the function if not booked
          }

          console.log("Appointment Details:", { id: appointmentSnapshot.id, ...appointmentData });

          // Store the office name & address in the state
          setAppointmentDetails({
          id: appointmentSnapshot.id,
          ...appointmentData,
          officeName: officeData.name,      
          officeAddress: officeData.address 
          });
          setLoading(false);
          return; // Exit the loop once the appointment is found
        }
        }

        // If the loop completes without finding the appointment, it doesn't exist
        console.log("Appointment not found in any office.");
        setLoading(false);
      } catch (error) {
        console.error("Error fetching appointment details:", error);
        setLoading(false);
      }
      };

      fetchAppointmentDetails(appointmentId);
    } else {
      setLoading(false);
    }
  }, [location]);

  // Safely convert to Date object if dateTime exists:
  const dateObj = appointmentDetails?.dateTime?.toDate();

  // Format date and time:
  const formattedDate = dateObj
    ? dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : 'N/A';

  const formattedTime = dateObj
    ? dateObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    : 'N/A';
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
    
      try {
        const db = getFirestore(app);
    
        // 1. Reference the original appointment doc
        const originalDocRef = doc(
          db,
          `offices/${officeID}/appointments`,
          appointmentDetails.id
        );
    
        // 2. Create a duplicate with canceled = true and include the reason
        // This is an AUDIT record of the canceled appointment
        const { id, officeId, officeName, officeAddress, ...originalFields } = appointmentDetails;
        await addDoc(collection(db, `offices/${officeID}/appointments`), {
          ...originalFields,
          booked: true,    // IMPORTANT: Keep as booked so it doesn't appear as available
          canceled: true,  // Mark as canceled for audit purposes
          cancellationReason: reason || 'No reason provided'
        });
    
    // 3. Revert the original doc back to defaults (keeping dateTime)
    await updateDoc(originalDocRef, {
      booked: false,
      canceled: false,
      isCheckedIn: false,
      isWalkIn: true,
      patientId: '',
      rescheduled: false,
      seenDoctor: false,
      vitalsDone: false
    });

    // Debug: Log patient info fetched from the appointment
    console.log("Patient details from appointment:", appointmentDetails.patientId);

    // Fetch patient email if missing in appointmentDetails
    if (!appointmentDetails.patient || !appointmentDetails.patient.email) {
      const patientDocRef = doc(db, 'patients', appointmentDetails.patientId);
      const patientDoc = await getDoc(patientDocRef);
      if (patientDoc.exists()) {
        appointmentDetails.patient = patientDoc.data();
      }
    }

    await sendCancellationEmail({
      patient: appointmentDetails.patient, // Now contains the email property
      dateTime: appointmentDetails.dateTime,
      location: { 
        name: appointmentDetails.officeName, 
        address: appointmentDetails.officeAddress
      },
      rescheduleURL: "", // Provide a reschedule URL if available
      cancellationReason: reason || "No reason provided"
    });

    // 4. Show success message
    setSuccess(true);
      } catch (error) {
        console.error('Error canceling appointment:', error);
      } finally {
        setLoading(false);
      }
    };
  
  if (loading) {
    return (
      <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <div style={styles.contentContainer}>
          <div style={styles.loadingMessage}>Loading appointment details...</div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <div style={styles.contentContainer}>
          <div style={styles.successAlert}>
            <h3 style={styles.alertTitle}>Appointment Cancelled</h3>
            <p>Your appointment has been successfully cancelled.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <Header />
      <div style={styles.contentContainer}>
        <div style={styles.header}>
          <h2 style={styles.title}>Cancel Your Appointment</h2>
          <p style={styles.subtitle}>Please confirm your cancellation details</p>
        </div>
        
        {appointmentDetails ? (
          <div style={styles.sectionCard}>
            <h4 style={styles.sectionTitle}>Appointment Details</h4>
            
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Office:</span>
              <span style={styles.detailValue}>
                <strong>{appointmentDetails.officeName}</strong> - {appointmentDetails.officeAddress}
              </span>
            </div>

            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Date:</span> 
              <span style={styles.detailValue}>
                {formattedDate}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Time:</span> 
              <span style={styles.detailValue}> 
                {formattedTime}
              </span>
            </div>
          </div>
        ) : (
          <div style={styles.errorAlert}>
            No appointment details found. Please check your email link.
          </div>
        )}

        <div style={styles.warningAlert}>
          <strong>Are you sure you want to cancel this appointment?</strong>
          <p>This action cannot be undone.</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label htmlFor="reason" style={styles.label}>
              Reason for cancellation (optional)
            </label>
            <textarea
              id="reason"
              rows="3"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={styles.textarea}
            ></textarea>
          </div>
          <div style={styles.buttonContainer}>
            <button type="submit" style={styles.cancelButton}>
              Confirm Cancellation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  contentContainer: {
    padding: "30px 40px 40px",
    maxWidth: "800px",
    margin: "0 auto",
  },
  header: {
    textAlign: "center",
    marginBottom: "1.5rem",
  },
  title: {
    margin: 0,
    color: "#333",
  },
  subtitle: {
    margin: 0,
    color: "#777",
  },
  sectionCard: {
    padding: "1rem",
    border: "1px solid #ccc",
    borderRadius: "8px",
    backgroundColor: "#fafafa",
    marginBottom: "1.5rem",
  },
  sectionTitle: {
    margin: "0 0 0.5rem",
    color: "#666",
  },
  detailRow: {
    marginBottom: "0.5rem",
    display: "flex",
  },
  detailLabel: {
    fontWeight: "600",
    width: "100px",
  },
  detailValue: {
    flex: 1,
  },
  form: {
    marginTop: "1.5rem",
  },
  formGroup: {
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "0.3rem",
    color: "#666",
  },
  textarea: {
    padding: "0.5rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontFamily: "inherit",
    minHeight: "100px",
    resize: "vertical",
    backgroundColor: "#ffffff",
    color: "#333333",
    colorScheme: "light only",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "1.5rem",
  },
  cancelButton: {
    padding: "0.6rem 1.2rem",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: "600",
  },
  successAlert: {
    padding: "1rem",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    color: "#155724",
    marginBottom: "1rem",
  },
  warningAlert: {
    padding: "1rem",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    borderRadius: "8px",
    color: "#856404",
    marginBottom: "1rem",
  },
  errorAlert: {
    padding: "1rem",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
    color: "#721c24",
    marginBottom: "1rem",
  },
  loadingMessage: {
    textAlign: "center",
    padding: "2rem",
    color: "#666",
  },
  alertTitle: {
    marginTop: 0,
  }
};

export default CancelPage;
